<template>
  <div class="form-pill__content">
    <aside v-if="!hideTabs" class="form-pill-left">
      <h2 class="form-pill-left__title">Pill Shape</h2>
      <ul class="form-pill-left__list">
        <li
          v-for="tab in tabs"
          :key="tab.key"
          :class="{
            'form-pill-left__item': true,
            incorrect: isMarkedAsIncorrect(tab),
            'incorrect-active': isMarkedAsIncorrect(tab) && shapeMismatchPopupVisible,
            active: form.shape === tab.key,
            disabled: freezeShape,
            'text-primary': isMarkedAsApplied(tab),
          }"
          @click="!freezeShape && setTab(tab.key)"
          @mouseenter="() => isMarkedAsIncorrect(tab) && showShapeMismatchPopup()"
        >
          <v-pill-shape-mismatch-popup
            class="mismatch-popup"
            v-if="isMarkedAsIncorrect(tab) && shapeMismatchPopupVisible"
            :database-shape-name="databaseShapeName"
            @close="shapeMismatchPopupVisible = false"
            @apply="onApplyMismatch"
            @ignore="onIgnoreMismatch"
          />
          <i class="icon">
            <img :src="getIcon(tab)" />
          </i>
          {{ tab.title }}
        </li>
      </ul>
    </aside>
    <div class="form-pill-right">
      <div v-if="form.shape === ''">
        <div class="pill-diemension-intro">
          <img src="~@/assets/pill-dimensions-intro.svg" />
          <div class="text">
            <h2>{{ $t("page.new.pill.title") }}</h2>
            <p>
              {{ $t("page.new.pill.description") }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="form.shape === 'capsule'" class="form-pill-right__content">
        <div class="form-pill-right__inputs">
          <div class="form-pill-right__input">
            <label class="label-form">Height h [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.capsule.h"
              :read-only="readOnly"
              input-id="capsule-h-input"
              ref="capsule-h"
              :validation="getValidationField('capsule', 'h')"
              :error-messages="{
                notEqual: '10.5 mm ⩽ h < 26 mm',
                between: '10.5 mm ⩽ h < 26 mm',
                biggerThan: 'h > d',
              }"
              :match-value="getMatchValue('capsule', 'h')"
              @mismatchApproved="approveMismatch('capsule', 'h')"
              @errorApproved="approveError('capsule', 'h')"
              :error-approved="isErrorApproved('capsule', 'h')"
              :mismatch-approved="isMismatchApproved('capsule', 'h')"
              @blur="handleInputBlur('capsule', 'h')"
              @focus="focusedRef = 'capsule-h'"
              :match-mode="matchMode"
            />
          </div>

          <div class="form-pill-right__input">
            <label class="label-form">Diameter d [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              input-id="capsule-d-input"
              v-model.number="form.properties.capsule.d"
              :read-only="readOnly"
              ref="capsule-d"
              :validation="getValidationField('capsule', 'd')"
              :error-messages="{
                between: '3 mm ⩽ d ⩽ 10.2 mm',
                biggerThan: 'h > d',
              }"
              :match-value="getMatchValue('capsule', 'd')"
              @mismatchApproved="approveMismatch('capsule', 'd')"
              @errorApproved="approveError('capsule', 'd')"
              :error-approved="isErrorApproved('capsule', 'd')"
              :mismatch-approved="isMismatchApproved('capsule', 'd')"
              @blur="handleInputBlur('capsule', 'd')"
              @focus="focusedRef = 'capsule-d'"
              :match-mode="matchMode"
            />
          </div>
        </div>

        <div class="form-pill-right__pills">
          <v-pill-shape-form-image
            image-path="01-capsule-0-base.svg"
            :refs="['capsule-h']"
            :focused="[isFocused('capsule', 'h')]"
            :invalid="[isInvalid('capsule', 'h')]"
            @imagePartClick="focusInput"
          />

          <v-pill-shape-form-image
            image-path="01-capsule-3-base.svg"
            :refs="['capsule-d']"
            :focused="[isFocused('capsule', 'd')]"
            :invalid="[isInvalid('capsule', 'd')]"
            @imagePartClick="focusInput"
          />
        </div>
      </div>

      <div v-if="form.shape === 'egg'" class="form-pill-right__content">
        <div class="form-pill-right__inputs">
          <div class="form-pill-right__input">
            <label class="label-form">Height h [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.egg.h"
              :read-only="readOnly"
              input-id="egg-h-input"
              ref="egg-h"
              :validation="getValidationField('egg', 'h')"
              :error-messages="{
                between: '3 mm ⩽ h ⩽ 11 mm',
                biggerThan: 'h > d',
              }"
              :match-value="getMatchValue('egg', 'h')"
              @mismatchApproved="approveMismatch('egg', 'h')"
              @errorApproved="approveError('egg', 'h')"
              :error-approved="isErrorApproved('egg', 'h')"
              :mismatch-approved="isMismatchApproved('egg', 'h')"
              @blur="handleInputBlur('egg', 'h')"
              @focus="focusedRef = 'egg-h'"
              :match-mode="matchMode"
            />
          </div>
          <div class="form-pill-right__input">
            <label class="label-form">Diameter d [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.egg.d"
              :read-only="readOnly"
              input-id="egg-d-input"
              ref="egg-d"
              :validation="getValidationField('egg', 'd')"
              :error-messages="{
                between: '3 mm ⩽ d ⩽ 8 mm',
                biggerThan: 'h > d',
              }"
              :match-value="getMatchValue('egg', 'd')"
              @mismatchApproved="approveMismatch('egg', 'd')"
              @errorApproved="approveError('egg', 'd')"
              :error-approved="isErrorApproved('egg', 'd')"
              :mismatch-approved="isMismatchApproved('egg', 'd')"
              @blur="handleInputBlur('egg', 'd')"
              @focus="focusedRef = 'egg-d'"
              :match-mode="matchMode"
            />
          </div>
        </div>
        <div class="form-pill-right__pills">
          <v-pill-shape-form-image
            image-path="02-egg-1-base.svg"
            :refs="['egg-h']"
            :focused="[isFocused('egg', 'h')]"
            :invalid="[isInvalid('egg', 'h')]"
            @imagePartClick="focusInput"
          />

          <v-pill-shape-form-image
            image-path="02-egg-3-base.svg"
            :refs="['egg-d']"
            :focused="[isFocused('egg', 'd')]"
            :invalid="[isInvalid('egg', 'd')]"
            @imagePartClick="focusInput"
          />
        </div>
      </div>

      <div v-if="form.shape === 'ball'" class="form-pill-right__content">
        <div class="form-pill-right__inputs">
          <div class="form-pill-right__input">
            <label class="label-form">Diameter d [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              input-id="ball-d-input"
              v-model.number="form.properties.ball.d"
              :read-only="readOnly"
              ref="ball-d"
              :validation="getValidationField('ball', 'd')"
              :error-messages="{
                between: '3 mm ⩽ d ⩽ 8 mm',
              }"
              :match-value="getMatchValue('ball', 'd')"
              @mismatchApproved="approveMismatch('ball', 'd')"
              @errorApproved="approveError('ball', 'd')"
              :error-approved="isErrorApproved('ball', 'd')"
              :mismatch-approved="isMismatchApproved('ball', 'd')"
              @blur="handleInputBlur('ball', 'd')"
              @focus="focusedRef = 'ball-d'"
              :match-mode="matchMode"
            />
          </div>
        </div>
        <div class="form-pill-right__pills">
          <v-pill-shape-form-image
            image-path="03-ball-1-base.svg"
            :refs="['ball-d']"
            :focused="[isFocused('ball', 'd')]"
            :invalid="[isInvalid('ball', 'd')]"
            @imagePartClick="focusInput"
          />
        </div>
      </div>

      <div v-if="form.shape === 'round'" class="form-pill-right__content form-pill-right__content--submenu">
        <ul v-if="!hideTabs" class="form-pill-right__submenu">
          <li
            @click="roundTabActive = 'not-convex'"
            :class="{
              'form-pill-right__submenu--active': roundTabActive === 'not-convex',
            }"
          >
            <img src="~@/assets/pill/not-convex.svg" />
            {{ $t("pill_shape.oval_not_convex") }}
          </li>
          <li
            @click="roundTabActive = 'convex'"
            :class="{
              'form-pill-right__submenu--active': roundTabActive === 'convex',
            }"
          >
            <img src="~@/assets/pill/convex.svg" />
            {{ $t("pill_shape.oval_convex") }}
          </li>
        </ul>
        <div>
          <div>
            <div class="form-pill-right__inputs">
              <div class="form-pill-right__input">
                <label class="label-form">Diameter d [mm]</label>
                <v-pill-shape-form-input
                  :disabled="shapeDismatch"
                  v-model.number="form.properties.round.d"
                  :read-only="readOnly"
                  ref="round-d"
                  :validation="getValidationField('round', 'd')"
                  :error-messages="{
                    between: '3 mm ⩽ d ⩽ 18 mm',
                    biggerThan: 'd ⩾ t1',
                  }"
                  :match-value="getMatchValue('round', 'd')"
                  @mismatchApproved="approveMismatch('round', 'd')"
                  @errorApproved="approveError('round', 'd')"
                  :error-approved="isErrorApproved('round', 'd')"
                  :mismatch-approved="isMismatchApproved('round', 'd')"
                  @blur="handleInputBlur('round', 'd')"
                  @focus="focusedRef = 'round-d'"
                  :match-mode="matchMode"
                />
              </div>

              <div class="form-pill-right__input">
                <label class="label-form">Thickness t1 [mm]</label>
                <v-pill-shape-form-input
                  :disabled="shapeDismatch"
                  v-model.number="form.properties.round.t1"
                  :read-only="readOnly"
                  ref="round-t1"
                  :validation="getValidationField('round', 't1')"
                  :error-messages="{
                    between: '1.5 mm ⩽ t1 ⩽ 8 mm',
                    biggerThan: 't1 ⩾ t2',
                    lowerThan: 't1 ⩽ d',
                  }"
                  :match-value="getMatchValue('round', 't1')"
                  @mismatchApproved="approveMismatch('round', 't1')"
                  @errorApproved="approveError('round', 't1')"
                  :error-approved="isErrorApproved('round', 't1')"
                  :mismatch-approved="isMismatchApproved('round', 't1')"
                  @blur="handleInputBlur('round', 't1')"
                  @focus="focusedRef = 'round-t1'"
                  :match-mode="matchMode"
                />
              </div>

              <div
                class="form-pill-right__input"
                :class="{
                  'form-pill-right__input--disabled': roundTabActive === 'not-convex',
                }"
              >
                <label class="label-form">Thickness t2 [mm]</label>
                <v-pill-shape-form-input
                  v-model.number="form.properties.round.t2"
                  :read-only="readOnly"
                  ref="round-t2"
                  :disabled="shapeDismatch || roundTabActive === 'not-convex'"
                  :validation="getValidationField('round', 't2')"
                  :error-messages="{
                    lowerThan: 't2 ⩽ t1',
                    aboveZero: '0 < t2',
                  }"
                  :match-value="getMatchValue('round', 't2')"
                  @mismatchApproved="approveMismatch('round', 't2')"
                  @errorApproved="approveError('round', 't2')"
                  :error-approved="isErrorApproved('round', 't2')"
                  :mismatch-approved="isMismatchApproved('round', 't2')"
                  @blur="handleInputBlur('round', 't2')"
                  @focus="focusedRef = 'round-t2'"
                  :match-mode="matchMode"
                />
              </div>
            </div>

            <div class="form-pill-right__pills">
              <v-pill-shape-form-image
                image-path="04-round-not-convex-1-base.svg"
                :refs="['round-d']"
                :focused="[isFocused('round', 'd')]"
                :invalid="[isInvalid('round', 'd')]"
                @imagePartClick="focusInput"
              />

              <v-pill-shape-form-image
                v-if="roundTabActive === 'not-convex'"
                image-path="04-round-not-convex-3-base.svg"
                :refs="['round-t1']"
                :focused="[isFocused('round', 't1')]"
                :invalid="[isInvalid('round', 't1')]"
                @imagePartClick="focusInput"
              />
              <v-pill-shape-form-image
                v-else
                class="pill-margin-top"
                image-path="05-round-convex-3-4-base.svg"
                :refs="['round-t1', 'round-t2']"
                :focused="[isFocused('round', 't1'), isFocused('round', 't2')]"
                :invalid="[isInvalid('round', 't1'), isInvalid('round', 't2')]"
                @imagePartClick="focusInput"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="form.shape === 'oval'" class="form-pill-right__content form-pill-right__content--submenu">
        <ul v-if="!hideTabs" class="form-pill-right__submenu form-pill-right__submenu--ver2">
          <li
            @click="ovalTabActive = 'not-convex'"
            :class="{
              'form-pill-right__submenu--active': ovalTabActive === 'not-convex',
            }"
          >
            <img src="~@/assets/pill/not-convex2.svg" />
            {{ $t("pill_shape.oval_not_convex") }}
          </li>
          <li
            @click="ovalTabActive = 'convex'"
            :class="{
              'form-pill-right__submenu--active': ovalTabActive === 'convex',
            }"
          >
            <img src="~@/assets/pill/convex2.svg" />
            {{ $t("pill_shape.oval_convex") }}
          </li>
        </ul>
        <div class="form-pill-right__inputs">
          <div class="form-pill-right__input">
            <label class="label-form">Height h [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.oval.h"
              :read-only="readOnly"
              ref="oval-h"
              :validation="getValidationField('oval', 'h')"
              :error-messages="{
                between: '3 mm ⩽ h ⩽ 18 mm',
                biggerThan: 'h ⩾ w1',
              }"
              :match-value="getMatchValue('oval', 'h')"
              @mismatchApproved="approveMismatch('oval', 'h')"
              @errorApproved="approveError('oval', 'h')"
              :error-approved="isErrorApproved('oval', 'h')"
              :mismatch-approved="isMismatchApproved('oval', 'h')"
              @blur="handleInputBlur('oval', 'h')"
              @focus="focusedRef = 'oval-h'"
              :match-mode="matchMode"
            />
          </div>

          <div class="form-pill-right__input">
            <label class="label-form">Width w1 [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.oval.w1"
              :read-only="readOnly"
              ref="oval-w1"
              :validation="getValidationField('oval', 'w1')"
              :error-messages="{
                between: '3 mm ⩽ w1 ⩽ 18 mm',
                biggerThan: 'w1 ⩾ w2',
              }"
              :match-value="getMatchValue('oval', 'w1')"
              @mismatchApproved="approveMismatch('oval', 'w1')"
              @errorApproved="approveError('oval', 'w1')"
              :error-approved="isErrorApproved('oval', 'w1')"
              :mismatch-approved="isMismatchApproved('oval', 'w1')"
              @blur="handleInputBlur('oval', 'w1')"
              @focus="focusedRef = 'oval-w1'"
              :match-mode="matchMode"
            />
          </div>

          <div
            class="form-pill-right__input"
            :class="{
              'form-pill-right__input--disabled': ovalTabActive === 'not-convex',
            }"
          >
            <label class="label-form">Width w2 [mm]</label>
            <v-pill-shape-form-input
              v-model.number="form.properties.oval.w2"
              :read-only="readOnly"
              ref="oval-w2"
              :disabled="shapeDismatch || ovalTabActive === 'not-convex'"
              :validation="getValidationField('oval', 'w2')"
              :error-messages="{
                lowerThan: 'w2 ⩽ w1',
                aboveZero: '0 < w2',
              }"
              :match-value="getMatchValue('oval', 'w2')"
              @mismatchApproved="approveMismatch('oval', 'w2')"
              @errorApproved="approveError('oval', 'w2')"
              :error-approved="isErrorApproved('oval', 'w2')"
              :mismatch-approved="isMismatchApproved('oval', 'w2')"
              @blur="handleInputBlur('oval', 'w2')"
              @focus="focusedRef = 'oval-w2'"
              :match-mode="matchMode"
            />
          </div>

          <div class="form-pill-right__input">
            <label class="label-form">Thickness t1 [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.oval.t1"
              :read-only="readOnly"
              ref="oval-t1"
              :validation="getValidationField('oval', 't1')"
              :error-messages="{
                between: '3 mm ⩽ t1 ⩽ 8 mm',
                biggerThan: 't1 ⩾ t2',
                lowerThan: 't1 ⩽ w1',
              }"
              :match-value="getMatchValue('oval', 't1')"
              @mismatchApproved="approveMismatch('oval', 't1')"
              @errorApproved="approveError('oval', 't1')"
              :error-approved="isErrorApproved('oval', 't1')"
              :mismatch-approved="isMismatchApproved('oval', 't1')"
              @blur="handleInputBlur('oval', 't1')"
              @focus="focusedRef = 'oval-t1'"
              :match-mode="matchMode"
            />
          </div>

          <div class="form-pill-right__input">
            <label class="label-form">Thickness t2 [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.oval.t2"
              :read-only="readOnly"
              ref="oval-t2"
              :validation="getValidationField('oval', 't2')"
              :error-messages="{
                lowerThan: 't2 ⩽ t1',
                aboveZero: '0 < t2',
              }"
              :match-value="getMatchValue('oval', 't2')"
              @mismatchApproved="approveMismatch('oval', 't2')"
              @errorApproved="approveError('oval', 't2')"
              :error-approved="isErrorApproved('oval', 't2')"
              :mismatch-approved="isMismatchApproved('oval', 't2')"
              @blur="handleInputBlur('oval', 't2')"
              @focus="focusedRef = 'oval-t2'"
              :match-mode="matchMode"
            />
          </div>

          <div class="form-pill-right__input">
            <label class="label-form">Diagonal c [mm]</label>
            <v-pill-shape-form-input
              :disabled="shapeDismatch"
              v-model.number="form.properties.oval.c"
              :read-only="readOnly || matchMode"
              ref="oval-c"
              :validation="getValidationField('oval', 'c')"
              :error-messages="{
                biggerThan: 'c ⩾ w1',
                sqrtRule: 'c ⩽ sqrt(w1^2 + t1^2)',
              }"
              :match-value="getMatchValue('oval', 'c')"
              @mismatchApproved="approveMismatch('oval', 'c')"
              @errorApproved="approveError('oval', 'c')"
              :error-approved="isErrorApproved('oval', 'c')"
              :mismatch-approved="isMismatchApproved('oval', 'c')"
              @blur="handleInputBlur('oval', 'c')"
              @focus="focusedRef = 'oval-c'"
              :match-mode="matchMode"
            />
          </div>
        </div>
        <div class="form-pill-right__pills form-pill-right__pills--groups">
          <div class="pill-group">
            <v-pill-shape-form-image
              v-if="ovalTabActive === 'not-convex'"
              class="pill-margin-left"
              image-path="06-oval-not-convex-1-2-base.svg"
              :refs="['oval-h', 'oval-w1']"
              :focused="[isFocused('oval', 'h'), isFocused('oval', 'w1')]"
              :invalid="[isInvalid('oval', 'h'), isInvalid('oval', 'w1')]"
              @imagePartClick="focusInput"
            />
            <v-pill-shape-form-image
              v-else
              image-path="07-oval-convex-1-2-3-base.svg"
              :refs="['oval-h', 'oval-w1', 'oval-w2']"
              :focused="[isFocused('oval', 'h'), isFocused('oval', 'w1'), isFocused('oval', 'w2')]"
              :invalid="[isInvalid('oval', 'h'), isInvalid('oval', 'w1'), isInvalid('oval', 'w2')]"
              @imagePartClick="focusInput"
            />
            <v-pill-shape-form-image
              image-path="06-oval-not-convex-4-5-base.svg"
              :refs="['oval-t1', 'oval-t2']"
              :focused="[isFocused('oval', 't1'), isFocused('oval', 't2')]"
              :invalid="[isInvalid('oval', 't1'), isInvalid('oval', 't2')]"
              @imagePartClick="focusInput"
            />
          </div>

          <v-pill-shape-form-image
            image-path="06-oval-not-convex-7-base.svg"
            :refs="['oval-c']"
            :focused="[isFocused('oval', 'c')]"
            :invalid="[isInvalid('oval', 'c')]"
            @imagePartClick="focusInput"
          />
        </div>
      </div>

      <div
        v-if="freezeShape && (isMarkedAsIncorrect({ key: form.shape }) || !isPillVerified)"
        class="form-pill-right__error text-red"
      >
        <span>{{ $t("page.pill-shape-form.incorrect-values") }}</span>
      </div>

      <div v-if="form.shape === 'special' || form.shape === 'specialform'">
        <v-pill-shape-form-special-shape :pill-form-data="pillFormData"></v-pill-shape-form-special-shape>
      </div>
    </div>
  </div>
</template>

<script>
import { between } from "vuelidate/lib/validators";
import VPillShapeFormInput from "./v-pill-shape-form-input";
import VPillShapeFormImage from "./v-pill-shape-form-image";
import VPillShapeMismatchPopup from "./v-pill-shape-mismatch-popup";
import VPillShapeFormSpecialShape from "./v-pill-shape-form-special-shape.vue";

const biggerThan = (param) => (value, vm) => parseFloat(vm[param]) < parseFloat(value);

const notEqual = (param) => (value) => parseFloat(value) !== parseFloat(param);

const biggerThanOrEqual = (param) => (value, vm) => {
  const otherValueFloat = parseFloat(vm[param]);
  const valueFloat = parseFloat(value);
  return isNaN(otherValueFloat) || isNaN(valueFloat) || otherValueFloat <= valueFloat;
};

const lowerThanOrEqual = (param) => (value, vm) => {
  const otherValueFloat = parseFloat(vm[param]);
  const valueFloat = parseFloat(value);
  return isNaN(otherValueFloat) || isNaN(valueFloat) || otherValueFloat >= valueFloat;
};

const greaterThanZero = (value) => value > 0;

const DIMENSIONS = {
  capsule: ["h", "d"],
  egg: ["h", "d"],
  ball: ["d"],
  round: ["d", "t1", "t2"],
  oval: ["h", "w1", "w2", "t1", "t2", "c"],
  special: [],
};

function mapPill(pill) {
  const properties = {};
  Object.keys(DIMENSIONS).forEach((shape) => {
    properties[shape] = {};
    DIMENSIONS[shape].forEach((d) => {
      properties[shape][d] = pill.shape === shape ? pill.dimensions[d] : null;
    });
  });

  return {
    shape: pill.shape,
    properties,
  };
}

export default {
  name: "VPillShapeForm",
  components: {
    VPillShapeFormImage,
    VPillShapeFormInput,
    VPillShapeMismatchPopup,
    VPillShapeFormSpecialShape,
  },
  props: {
    hideTabs: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    convex: {
      type: Boolean,
      default: false,
    },
    matchMode: {
      type: Boolean,
      default: false,
    },
    pill: {
      type: Object,
      default: () => ({
        shape: "",
        dimensions: {},
      }),
    },
    matchPill: {
      type: Object,
      default: () => ({
        shape: "",
        dimensions: {},
      }),
    },
    shapeDismatch: {
      type: Boolean,
      default: false,
    },
    databaseShapeName: {
      type: String,
      default: null,
    },
    approvedMismatches: {
      type: Array,
      default: () => [],
    },
    approvedErrors: {
      type: Array,
      default: () => [],
    },
    freezeShape: {
      type: Boolean,
      default: false,
    },
    pillFormData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      manufacturers: [],
      focusedRef: "",
      roundTabActive: this.convex ? "convex" : "not-convex",
      ovalTabActive: this.convex ? "convex" : "not-convex",
      form: mapPill(this.pill),
      matchForm: this.matchPill ? mapPill(this.matchPill) : null,
      shapeMismatchPopupVisible: false,
      shapeMismatchApplied: false,
      isPillVerified: undefined,
    };
  },
  computed: {
    isConvex() {
      switch (this.form.shape) {
        case "oval":
          return this.ovalTabActive === "convex";
        case "round":
          return this.roundTabActive === "convex";
        default:
          return null;
      }
    },
    tabs() {
      return [
        {
          key: "capsule",
          title: "Capsule",
          icon: require("../../assets/pill/capsule.svg"),
          iconRed: require("../../assets/pill/capsule-red.svg"),
          iconBlue: require("../../assets/pill/capsule-blue.svg"),
        },
        {
          key: "egg",
          title: "Egg",
          icon: require("../../assets/pill/egg.svg"),
          iconRed: require("../../assets/pill/egg-red.svg"),
          iconBlue: require("../../assets/pill/egg-blue.svg"),
        },
        {
          key: "ball",
          title: "Ball",
          icon: require("../../assets/pill/ball.svg"),
          iconRed: require("../../assets/pill/ball-red.svg"),
          iconBlue: require("../../assets/pill/ball-blue.svg"),
        },
        {
          key: "round",
          title: "Round",
          icon: require("../../assets/pill/round.svg"),
          iconRed: require("../../assets/pill/round-red.svg"),
          iconBlue: require("../../assets/pill/round-blue.svg"),
        },
        {
          key: "oval",
          title: "Oval",
          icon: require("../../assets/pill/oval.svg"),
          iconRed: require("../../assets/pill/oval-red.svg"),
          iconBlue: require("../../assets/pill/oval-blue.svg"),
        },
        {
          key: "special",
          title: "Special",
          icon: require("../../assets/pill/special.svg"),
          iconRed: require("../../assets/pill/special-red.svg"),
          iconBlue: require("../../assets/pill/special-blue.svg"),
        },
      ];
    },
  },
  methods: {
    setTab(shape) {
      this.form.shape = this.form.shape === shape ? "" : shape;
    },
    isInvalid(shape, dimension) {
      if (this.matchMode) {
        return !this.isVerified(dimension);
      } else {
        if (this.readOnly) {
          return false;
        }
        const field = this.getValidationField(shape, dimension);
        return field.$invalid && field.$dirty;
      }
    },
    isFocused(shape, dimension) {
      return this.focusedRef === `${this.form.shape}-${dimension}`;
    },
    focusInput(ref) {
      this.$refs[ref].$el.querySelector("input").focus();
    },
    handleInputBlur(shape, dimension) {
      this.getValidationField(shape, dimension).$touch();
      this.focusedRef = "";
    },
    getValidationField(shape, dimension) {
      return this.$v.form.properties[shape][dimension];
    },
    getValue(shape, dimension) {
      return +this.form.properties[shape][dimension];
    },
    getMatchValue(shape, dimension) {
      return this.matchForm ? +(this.matchForm.properties[shape][dimension] || 0) : null;
    },
    approveMismatch(shape, dimension) {
      this.approvedMismatches.push({ shape, dimension });
      this.emitUpdate();
    },
    isMismatchApproved(shape, dimension) {
      return !!this.approvedMismatches.find((e) => e.dimension === dimension && e.shape === shape);
    },
    approveError(shape, dimension) {
      this.approvedErrors.push({ shape, dimension });
      this.emitUpdate();
    },
    isErrorApproved(shape, dimension) {
      return !!this.approvedErrors.find((e) => e.dimension === dimension && e.shape === shape);
    },
    isVerified(dimension) {
      const shape = this.form.shape;
      const dimensionValid = !this.getValidationField(shape, dimension).$invalid;
      const errorApproved = this.isErrorApproved(shape, dimension);

      if (!this.matchForm) {
        return dimensionValid || errorApproved;
      }

      const dimensionMatch = this.getValue(shape, dimension) === this.getMatchValue(shape, dimension);
      const dismatchApproved = this.isMismatchApproved(shape, dimension);

      return (dimensionValid || errorApproved) && (dimensionMatch || dismatchApproved);
    },
    emitUpdate() {
      const data = {
        pill: {
          shape: this.form.shape,
          isConvex: this.isConvex,
          properties: this.form.properties[this.form.shape],
        },
      };
      if (this.matchMode) {
        this.isPillVerified = DIMENSIONS[this.form.shape].every(this.isVerified);
        data.isPillVerified = this.isPillVerified;
      }
      this.$emit("update", data);
    },
    getIcon(tab) {
      if (this.isMarkedAsApplied(tab)) {
        return tab.iconBlue;
      } else if (this.isMarkedAsIncorrect(tab)) {
        return tab.iconRed;
      } else {
        return tab.icon;
      }
    },
    isMarkedAsIncorrect(tab) {
      return this.form.shape === tab.key && this.shapeDismatch;
    },
    isMarkedAsApplied(tab) {
      return this.form.shape === tab.key && this.shapeMismatchApplied;
    },
    showShapeMismatchPopup() {
      this.shapeMismatchPopupVisible = true;
    },
    onApplyMismatch() {
      Object.assign(this.form, this.matchForm);
      this.matchForm = null;
      this.shapeMismatchApplied = true;
      this.$emit("apply");
    },
    onIgnoreMismatch() {
      this.matchForm = null;
      this.shapeMismatchApplied = true;
      this.$emit("ignore");
      this.emitUpdate();
    },
  },
  validations: {
    form: {
      properties: {
        capsule: {
          h: {
            notEqual: notEqual(26),
            between: between(10.5, 26),
            biggerThan: biggerThan("d"),
          },
          d: {
            between: between(3, 10.2),
          },
        },
        egg: {
          h: {
            between: between(3, 11),
            biggerThan: biggerThan("d"),
          },
          d: {
            between: between(3, 8),
          },
        },
        ball: {
          d: {
            between: between(3, 8),
          },
        },
        round: {
          d: {
            between: between(3, 18),
            biggerThan: biggerThanOrEqual("t1"),
          },
          t1: {
            between: between(1.5, 8),
            biggerThan: biggerThanOrEqual("t2"),
            lowerThan: lowerThanOrEqual("d"),
          },
          t2: {
            aboveZero: greaterThanZero,
            lowerThan: lowerThanOrEqual("t1"),
          },
        },
        oval: {
          h: {
            between: between(3, 18),
            biggerThan: biggerThanOrEqual("w1"),
          },
          c: {
            biggerThan: biggerThanOrEqual("w1"),
            sqrtRule: (value, vm) => {
              const w1 = parseFloat(vm["w1"]);
              const t1 = parseFloat(vm["t1"]);
              const c = parseFloat(value);
              return isNaN(w1) || isNaN(t1) || isNaN(c) || c <= Math.sqrt(w1 * w1 + t1 * t1);
            },
          },
          w1: {
            between: between(3, 18),
            biggerThan: biggerThanOrEqual("w2"),
          },
          w2: {
            aboveZero: greaterThanZero,
            lowerThan: lowerThanOrEqual("w1"),
          },
          t1: {
            between: between(1.5, 8),
            biggerThan: biggerThanOrEqual("t2"),
            lowerThan: lowerThanOrEqual("w1"),
          },
          t2: {
            aboveZero: greaterThanZero,
            lowerThan: lowerThanOrEqual("t1"),
          },
        },
      },
    },
  },
  watch: {
    "form.properties.round.t1": function (val) {
      if (this.roundTabActive === "not-convex") {
        this.form.properties.round.t2 = val;
      }
    },
    "form.properties.oval.w1": function (val) {
      if (this.ovalTabActive === "not-convex") {
        this.form.properties.oval.w2 = val;
      }
    },
    form: {
      handler: function () {
        this.emitUpdate();
      },
      immediate: true,
      deep: true,
    },
    isConvex: "emitUpdate",
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";

.form-pill {
  margin: 90px auto;
  width: 1198px;
  &__top {
    background: #fff;
    border: 1px solid #ececec;
  }
  &__content {
    display: flex;
    border: 1px solid #ececec;
    margin: 15px 0 0 0;
  }
}
.form-pill-left {
  background: #fff;
  width: 227px;
  border-right: 1px solid #ececec;
  padding: 46px 0 45px 32px;
  &__title {
    font-size: 16px;
    line-height: 17px;
    color: #333333;
  }
  &__list {
    margin: 25px 0 0 0;
  }
  &__item {
    color: #333;
    font-size: 13px;
    padding: 19px 0 19px 86px;
    margin: 0 0 11px -32px;
    cursor: pointer;
    position: relative;
    opacity: 0.5;
    transition: all 0.3s;
    &.incorrect {
      color: $color-red;
    }
    &.incorrect-active::after {
      background: $color-red;
    }
    .icon {
      position: absolute;
      left: 32px;
      top: 50%;
      transform: translateY(-50%);
      text-align: center;
      width: 38px;
    }
    &::after {
      position: absolute;
      left: 0;
      top: 50%;
      background: #d15421;
      width: 4px;
      height: 32px;
      margin-top: -16px;
      content: "";
      opacity: 0;
      transition: all 0.3s;
    }
    &:hover {
      opacity: 0.9;
      &::after {
        opacity: 0.9;
      }
    }
    &.active {
      opacity: 1;
      &::after {
        opacity: 1;
      }
    }

    &.disabled {
      cursor: default !important;

      &:not(.active) {
        opacity: 0.5 !important;
        &::after {
          opacity: 0 !important;
        }
      }
    }
  }
}
.form-pill-right {
  background: #fdfdfd;
  width: calc(100% - 227px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-direction: column;
  &__content {
    margin-bottom: auto;
    padding-top: 100px;
    opacity: 0;
    animation: fade 0.5s forwards;
    &--submenu {
      padding-top: 132px;
      @media all and (max-width: 1200px) {
        padding-top: 105px;
      }
    }
  }
  &__inputs {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
  &__input {
    margin: 0 4px;
    transition: all 0.3s;
    position: relative;
    @media all and (max-width: 1200px) {
      margin-bottom: 10px;
    }
    .label-form {
      font-weight: bold;
      font-size: 13px;
      line-height: 14px;
      letter-spacing: 0.433333px;
      color: #999999;
      display: block;
      margin: 3px 0 7px 0;
    }
    &--disabled {
      opacity: 0.5;
    }
  }
  &__pills {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin: 74px 0 0 0;
    > div {
      margin: 0 16px;
      &.pill-margin-top {
        margin-top: -43px;
      }
      .pill-margin-left {
        margin-left: -38px;
      }
      &.pill-group {
        width: 343px;
        margin: 0 -20px 0 -18px;
        > div:first-child:not(.pill-margin-left) {
          margin-left: -2px;
          margin-bottom: 21px;
        }
        > div:not(:last-child) {
          margin-bottom: 22px;
        }
      }
    }
    &--groups {
      align-items: flex-end;
      margin-top: 28px;
      @media all and (max-width: 1200px) {
        margin-top: 20px;
      }
    }
  }
  &__submenu {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    background: #fff;
    border-bottom: 1px solid #ececec;
    padding: 0 53px;
    display: flex;
    li {
      padding: 32px 15px 32px 40px;
      position: relative;
      font-size: 13px;
      font-weight: bold;
      cursor: pointer;
      letter-spacing: 0.6px;
      margin: 0 27px 0 0;
      opacity: 0.5;
      img {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }
      &::after {
        position: absolute;
        left: 50%;
        bottom: 0;
        background: #d15421;
        width: 40px;
        height: 4px;
        content: "";
        opacity: 0;
        margin-left: -10px;
        transition: all 0.3s;
      }
      &:hover {
        opacity: 1;
        &::after {
          opacity: 1;
        }
      }
    }
    &--ver2 {
      img {
        left: -10px !important;
      }
    }
    &--active {
      opacity: 1 !important;
      &::after {
        opacity: 1 !important;
      }
    }
  }
  &__error {
    margin: 16px;
  }
}

.mismatch-popup {
  position: absolute;
}

.pill-diemension-intro {
  display: flex;
  align-items: center;
  margin: 0 auto;
  max-width: 420px;
  .text {
    padding: 0 0 0 30px;
    h2 {
      font-size: 16px;
      line-height: 17px;
      color: #000000;
      margin: 0;
    }
    p {
      font-size: 13px;
      line-height: 16px;
      letter-spacing: 0.464286px;
      color: #000000;
      margin: 0;
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
